import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Tooltip, message, Popover } from 'antd';
import { fetchTaskAiSummary, getDownloadUrl } from '../../api/projectManagementApi';
import { useTaskDrawer } from '../TaskDrawerContext';
import Avatar from './Avatar';
import { getFullName } from '../utils/personUtils';

/**
 * Returns icon and styling based on the semantic bullet category.
 */
function getBulletStyle(type = 'progress') {
    switch (type) {
        case 'blocker':
            return {
                icon: 'error',
                color: 'text-rose-600 dark:text-rose-400',
                bg: 'bg-rose-50 dark:bg-rose-950/50',
                border: 'border-rose-200 dark:border-rose-900/60',
            };
        case 'decision':
            return {
                icon: 'check_circle',
                color: 'text-emerald-600 dark:text-emerald-400',
                bg: 'bg-emerald-50 dark:bg-emerald-950/50',
                border: 'border-emerald-200 dark:border-emerald-900/60',
            };
        case 'action':
            return {
                icon: 'bolt',
                color: 'text-amber-600 dark:text-amber-400',
                bg: 'bg-amber-50 dark:bg-amber-950/50',
                border: 'border-amber-200 dark:border-amber-900/60',
            };
        case 'feedback':
            return {
                icon: 'forum',
                color: 'text-indigo-600 dark:text-indigo-400',
                bg: 'bg-indigo-50 dark:bg-indigo-950/50',
                border: 'border-indigo-200 dark:border-indigo-900/60',
            };
        case 'file':
            return {
                icon: 'attach_file',
                color: 'text-blue-600 dark:text-blue-400',
                bg: 'bg-blue-50 dark:bg-blue-950/50',
                border: 'border-blue-200 dark:border-blue-900/60',
            };
        case 'progress':
        default:
            return {
                icon: 'trending_up',
                color: 'text-[#03488a] dark:text-[#a7c8ff]',
                bg: 'bg-blue-50 dark:bg-blue-950/50',
                border: 'border-blue-200 dark:border-blue-900/60',
            };
    }
}

/**
 * Clickable file chip inside bullet text.
 */
function FileChip({ name, fileId, taskId, isRevision = false }) {
    const [opening, setOpening] = useState(false);

    const handleClick = async (e) => {
        e.stopPropagation();
        if (!fileId && !taskId) {
            message.info(`File reference: ${name}`);
            return;
        }

        setOpening(true);
        try {
            const isValidId = fileId && /^[a-f0-9]{24}$/i.test(String(fileId));
            if (isValidId && taskId && taskId !== 'new') {
                const { data } = await getDownloadUrl(taskId, fileId, 'inline');
                if (data?.result?.downloadUrl) {
                    window.open(data.result.downloadUrl, '_blank', 'noopener,noreferrer');
                    return;
                }
            }
            message.info(`Referenced: ${name}`);
        } catch {
            message.warning(`Could not open ${name} directly.`);
        } finally {
            setOpening(false);
        }
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={opening}
            className={`inline-flex items-center gap-1 mx-1 px-2 py-0.5 rounded-md text-xs font-semibold tracking-tight transition-all cursor-pointer ${isRevision
                    ? 'bg-purple-100 hover:bg-purple-200 dark:bg-purple-950/70 dark:hover:bg-purple-900/80 text-purple-800 dark:text-purple-300 border border-purple-200 dark:border-purple-800'
                    : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-[#03488a] dark:text-[#a7c8ff] border border-slate-200 dark:border-slate-700'
                }`}
            title={`Open ${name}`}
        >
            <span className="material-symbols-outlined text-xs leading-none">
                {isRevision ? 'history_edu' : 'draft'}
            </span>
            <span className="truncate max-w-[170px]">{name}</span>
            {opening && <span className="material-symbols-outlined text-xs animate-spin">sync</span>}
        </button>
    );
}

/**
 * Interactive hover profile card for team members mentioned in the summary.
 */
function PersonMentionHoverCard({ name, person }) {
    if (!person) {
        return <span className="font-semibold text-slate-800 dark:text-slate-100">{name}</span>;
    }

    const popoverContent = (
        <div className="w-64 p-3 font-body text-xs">
            <div className="flex items-center gap-2.5 pb-2 border-b border-slate-100 dark:border-slate-800">
                <Avatar person={person} name={name} size={36} />
                <div className="min-w-0 flex-1">
                    <p className="font-bold text-slate-900 dark:text-white truncate text-sm">
                        {getFullName(person) || name}
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                        {person.department ? `${person.department} · ` : ''}{person.role || person.adminRole || 'Team Member'}
                    </p>
                </div>
            </div>
            <div className="pt-2 space-y-1 text-[11px] text-slate-600 dark:text-slate-300">
                {person.email && (
                    <div className="flex items-center gap-1.5 truncate">
                        <span className="material-symbols-outlined text-[14px] text-slate-400">mail</span>
                        <span className="truncate">{person.email}</span>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <Popover content={popoverContent} trigger="hover" placement="top">
            <span className="font-bold text-slate-800 dark:text-slate-100 hover:text-[#03488a] dark:hover:text-[#a7c8ff] cursor-pointer underline decoration-dotted decoration-blue-400 underline-offset-2 transition-colors">
                {name}
            </span>
        </Popover>
    );
}

/**
 * Parses bullet text to render bracketed [FileName.ext], interactive person hover cards,
 * and bold anchor deep-links.
 */
function FormattedBulletText({ text, fileRefs = [], taskId, anchor, jumpToComment, jumpToAttachment, allCandidates = [] }) {
    if (!text) return null;

    // Helper to wrap person names in a text fragment
    const wrapPersons = (fragment, keyPrefix) => {
        if (!fragment) return null;
        const found = allCandidates.filter(p => p?.name && fragment.includes(p.name));
        if (found.length === 0) return fragment;

        const namesPattern = found.map(p => p.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
        const re = new RegExp(`\\b(${namesPattern})\\b`, 'g');
        const elements = [];
        let lIndex = 0;
        let m;
        while ((m = re.exec(fragment)) !== null) {
            if (m.index > lIndex) {
                elements.push(fragment.substring(lIndex, m.index));
            }
            const matchedName = m[1];
            const personObj = found.find(p => p.name === matchedName);
            elements.push(
                <PersonMentionHoverCard key={`${keyPrefix}-person-${m.index}`} name={matchedName} person={personObj} />
            );
            lIndex = re.lastIndex;
        }
        if (lIndex < fragment.length) {
            elements.push(fragment.substring(lIndex));
        }
        return elements;
    };

    // Helper to render text with anchor link if matching phrase exists
    const renderContentWithAnchor = (rawText) => {
        if (anchor?.id && anchor?.label && rawText.includes(anchor.label)) {
            const idx = rawText.indexOf(anchor.label);
            const before = rawText.substring(0, idx);
            const labelText = anchor.label;
            const after = rawText.substring(idx + labelText.length);

            return (
                <span>
                    {wrapPersons(before, 'pre')}
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (anchor.type === 'comment') jumpToComment?.(anchor.id);
                            else if (anchor.type === 'file') jumpToAttachment?.(anchor.id);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                if (anchor.type === 'comment') jumpToComment?.(anchor.id);
                                else if (anchor.type === 'file') jumpToAttachment?.(anchor.id);
                            }
                        }}
                        className="font-bold text-[#03488a] dark:text-[#a7c8ff] hover:underline cursor-pointer inline-flex items-center gap-0.5 group/link"
                        title={anchor.type === 'comment' ? 'Jump to comment in Comments tab' : 'Jump to attachment'}
                    >
                        <span>{labelText}</span>
                        <span className="material-symbols-outlined text-[13px] opacity-75 group-hover/link:opacity-100 transition-opacity">
                            arrow_outward
                        </span>
                    </span>
                    {wrapPersons(after, 'post')}
                </span>
            );
        }
        return wrapPersons(rawText, 'raw');
    };

    const parts = [];
    const regex = /\[([^\]]+)\]/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push({ type: 'text', content: text.substring(lastIndex, match.index) });
        }

        const tagContent = match[1].trim();
        const isRevision = /revision/i.test(tagContent);

        const matchedRef = fileRefs.find((r) => {
            const rName = (r.name || '').toLowerCase();
            const tName = tagContent.toLowerCase();
            return rName === tName || rName.includes(tName) || tName.includes(rName);
        });

        parts.push({
            type: 'chip',
            name: tagContent,
            fileId: matchedRef?.fileId,
            isRevision,
        });

        lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
        parts.push({ type: 'text', content: text.substring(lastIndex) });
    }

    const hasRenderedAnchor = anchor?.label && text.includes(anchor.label);

    return (
        <span>
            {parts.map((part, idx) => {
                if (part.type === 'chip') {
                    return (
                        <FileChip
                            key={idx}
                            name={part.name}
                            fileId={part.fileId}
                            taskId={taskId}
                            isRevision={part.isRevision}
                        />
                    );
                }
                return <span key={idx}>{renderContentWithAnchor(part.content)}</span>;
            })}
            {!hasRenderedAnchor && anchor?.id && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (anchor.type === 'comment') jumpToComment?.(anchor.id);
                        else if (anchor.type === 'file') jumpToAttachment?.(anchor.id);
                    }}
                    className="inline-flex items-center gap-0.5 ml-1.5 px-1.5 py-0.5 bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/70 dark:hover:bg-blue-900/80 text-[#03488a] dark:text-[#a7c8ff] border border-blue-200/80 dark:border-blue-800/60 rounded text-[10px] font-bold cursor-pointer transition-colors align-middle"
                    title={anchor.type === 'comment' ? 'Jump to comment' : 'Jump to file'}
                >
                    <span className="material-symbols-outlined text-[11px]">link</span>
                    <span>{anchor.type === 'comment' ? 'View Comment' : 'View File'}</span>
                </button>
            )}
        </span>
    );
}

// Client-side memory cache across drawer opens (eliminates unnecessary HTTP calls)
const clientOverviewCache = new Map();

/**
 * AI Task Overview Component
 * Positioned at the top of the Task Details Drawer body.
 * Provides a perspective-aware, compact 3-7 bullet summary of everything important.
 */
export default function AiTaskOverview({ onOpenFullSummary }) {
    const { task, jumpToComment, jumpToAttachment, employees = [] } = useTaskDrawer();
    const taskId = task?._id;

    const [isCollapsed, setIsCollapsed] = useState(() => {
        try {
            const stored = localStorage.getItem('dymax_ai_overview_collapsed');
            // Default is TRUE (collapsed) — user must explicitly expand
            return stored === null ? true : stored === 'true';
        } catch {
            return true;
        }
    });
    const [loading, setLoading] = useState(false);
    const [summaryData, setSummaryData] = useState(() => {
        if (!taskId) return null;
        if (clientOverviewCache.has(taskId)) return clientOverviewCache.get(taskId);
        if (task?.aiSummary?.structuredData) {
            clientOverviewCache.set(taskId, task.aiSummary.structuredData);
            return task.aiSummary.structuredData;
        }
        return null;
    });
    const [error, setError] = useState(null);
    const inFlightAiSummaryRef = useRef(null);
    const [lastFetchedTaskId, setLastFetchedTaskId] = useState(() => (taskId && (clientOverviewCache.has(taskId) || task?.aiSummary?.structuredData) ? taskId : null));

    const toggleCollapse = () => {
        setIsCollapsed((prev) => {
            const next = !prev;
            try {
                localStorage.setItem('dymax_ai_overview_collapsed', String(next));
            } catch { }
            return next;
        });
    };

    const loadSummary = useCallback(async (force = false) => {
        if (!taskId) return;
        if (!force && inFlightAiSummaryRef.current === taskId) return;

        // If not force, check client cache first
        if (!force && clientOverviewCache.has(taskId)) {
            setSummaryData(clientOverviewCache.get(taskId));
            setLastFetchedTaskId(taskId);
            return;
        }

        // If not force, check if the task already arrived with structuredData
        if (!force && task?.aiSummary?.structuredData) {
            setSummaryData(task.aiSummary.structuredData);
            clientOverviewCache.set(taskId, task.aiSummary.structuredData);
            setLastFetchedTaskId(taskId);
            return;
        }

        inFlightAiSummaryRef.current = taskId;
        setLoading(true);
        setError(null);
        try {
            const { data } = await fetchTaskAiSummary(taskId, force);
            if (data?.summary) {
                setSummaryData(data.summary);
                clientOverviewCache.set(taskId, data.summary);
            } else if (data?.result) {
                const fallbackObj = {
                    compactBullets: [
                        { text: 'Task activity updated. Click View Full Summary for details.', type: 'progress' }
                    ]
                };
                setSummaryData(fallbackObj);
                clientOverviewCache.set(taskId, fallbackObj);
            }
            if (data?.rateLimited && data?.message) {
                message.info(data.message);
            }
            setLastFetchedTaskId(taskId);
        } catch (err) {
            setError(err?.response?.data?.message || 'Unable to generate AI Overview.');
        } finally {
            inFlightAiSummaryRef.current = null;
            setLoading(false);
        }
    }, [taskId, task?.aiSummary?.structuredData]);

    useEffect(() => {
        if (!taskId) return;

        // Restore from client cache or task object immediately if available
        if (clientOverviewCache.has(taskId)) {
            const cached = clientOverviewCache.get(taskId);
            setSummaryData(cached);
            setLastFetchedTaskId(taskId);
        } else if (task?.aiSummary?.structuredData) {
            setSummaryData(task.aiSummary.structuredData);
            clientOverviewCache.set(taskId, task.aiSummary.structuredData);
            setLastFetchedTaskId(taskId);
        } else if (taskId !== lastFetchedTaskId && inFlightAiSummaryRef.current !== taskId) {
            loadSummary(false);
        }
    }, [taskId, lastFetchedTaskId, loadSummary, task?.aiSummary?.structuredData]);

    // Build candidates map for matching mentioned people to hover profile cards
    const allCandidates = useMemo(() => {
        const map = new Map();

        const extractDetails = (p) => {
            if (!p) return null;
            if (typeof p === 'string') {
                const trimmed = p.trim();
                if (!trimmed || trimmed.length < 2 || /^[0-9a-fA-F]{24}$/.test(trimmed)) return null;
                return {
                    _id: null,
                    name: trimmed,
                    email: '',
                    role: 'Team Member',
                    department: '',
                    avatar: '',
                };
            }
            if (typeof p !== 'object') return null;

            let rawName = '';
            if (typeof p.name === 'string') {
                rawName = p.name.trim();
            } else if (p.name && typeof p.name === 'object') {
                rawName = extractDetails(p.name)?.name || '';
            }

            const surname = typeof p.surname === 'string' ? p.surname.trim() : '';
            const firstName = typeof p.firstName === 'string' ? p.firstName.trim() : '';
            const lastName = typeof p.lastName === 'string' ? p.lastName.trim() : '';

            let combinedName = rawName;
            if (!combinedName) {
                combinedName = [firstName, lastName].filter(Boolean).join(' ').trim();
            }
            if (surname && !combinedName.includes(surname)) {
                combinedName = [combinedName, surname].filter(Boolean).join(' ').trim();
            }
            if (!combinedName && typeof p.email === 'string') {
                combinedName = p.email.split('@')[0];
            }
            if (!combinedName || combinedName.length < 2) return null;

            const id = p._id || p.id || null;
            const role = (typeof p.role === 'object' ? p.role?.name : p.role) || p.jobTitle || p.adminRole || 'Team Member';
            const department = (typeof p.department === 'object' ? p.department?.name : p.department) || '';
            const email = typeof p.email === 'string' ? p.email : '';
            const avatar = typeof p.avatar === 'string' ? p.avatar : (typeof p.photo === 'string' ? p.photo : '');

            return {
                _id: id ? String(id) : null,
                name: combinedName,
                email,
                role: typeof role === 'string' ? role : 'Team Member',
                department: typeof department === 'string' ? department : '',
                avatar,
            };
        };

        const addPerson = (raw) => {
            const person = extractDetails(raw);
            if (!person || !person.name) return;
            const lower = person.name.toLowerCase();
            if (!map.has(lower)) {
                map.set(lower, person);
            }
            const parts = person.name.split(' ');
            if (parts.length > 1 && parts[0].length >= 3) {
                const firstName = parts[0];
                const fLower = firstName.toLowerCase();
                if (!map.has(fLower)) {
                    map.set(fLower, { ...person, name: firstName });
                }
            }
        };

        (employees || []).forEach(addPerson);
        if (task?.assignee) addPerson(task.assignee);
        (task?.collaborators || []).forEach(addPerson);
        (task?.reviewers || []).forEach(addPerson);
        (task?.comments || []).forEach((c) => {
            if (!c) return;
            if (c.user) addPerson(c.user);
            if (c.author) addPerson(c.author);
        });
        (summaryData?.mentionedPeople || []).forEach((mp) => {
            if (!mp) return;
            if (typeof mp === 'string') addPerson(mp);
            else if (mp.name) addPerson(mp);
        });

        return Array.from(map.values());
    }, [employees, task, summaryData]);

    if (!task) return null;

    const bullets = summaryData?.compactBullets || [];
    const viewerPerspective = summaryData?.viewerPerspective || 'You';

    return (
        <div data-tour="ai-task-overview" className="relative rounded-2xl bg-gradient-to-b from-[#f8faff] via-white to-white dark:from-[#1b2230] dark:via-[#161b26] dark:to-[#161b26] border border-blue-100/80 dark:border-blue-900/40 shadow-xs overflow-hidden transition-all duration-300">
            {/* Top decorative gradient bar */}
            <div className="h-1 w-full bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500" />

            <div className="p-4 sm:p-5 space-y-3.5">
                {/* ── Header ────────────────────────────────────────── */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-purple-600 via-indigo-600 to-blue-600 flex items-center justify-center text-white shadow-xs">
                            <span className="material-symbols-outlined text-[19px]">auto_awesome</span>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm font-extrabold text-slate-900 dark:text-white font-headline tracking-tight">
                                    AI Task Overview
                                </h3>
                                {/* <span className="inline-flex items-center gap-1 text-[10px] font-bold text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-950/70 px-2 py-0.5 rounded-full border border-purple-200/80 dark:border-purple-800/60">
                                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse" />
                                    AI Task Intelligence
                                </span> */}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-1">
                        <Tooltip title={isCollapsed ? "Expand Overview" : "Collapse Overview"}>
                            <button
                                type="button"
                                onClick={toggleCollapse}
                                className="p-1.5 text-slate-400 hover:text-[#03488a] dark:hover:text-blue-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                            >
                                <span className="material-symbols-outlined text-base">
                                    {isCollapsed ? 'expand_more' : 'expand_less'}
                                </span>
                            </button>
                        </Tooltip>

                        <Tooltip title="Refresh AI Overview">
                            <button
                                type="button"
                                onClick={() => loadSummary(true)}
                                disabled={loading}
                                className="p-1.5 text-slate-400 hover:text-[#03488a] dark:hover:text-blue-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer disabled:opacity-50"
                            >
                                <span className={`material-symbols-outlined text-base ${loading ? 'animate-spin' : ''}`}>
                                    refresh
                                </span>
                            </button>
                        </Tooltip>
                    </div>
                </div>

                {/* ── Collapsed Teaser ──────────────────────────────── */}
                {isCollapsed && (
                    <div
                        onClick={toggleCollapse}
                        className="px-3.5 py-2 bg-slate-50 dark:bg-slate-900/40 rounded-xl cursor-pointer hover:bg-blue-50/60 dark:hover:bg-slate-800/60 transition-colors flex items-center justify-between text-xs text-slate-600 dark:text-slate-400 border border-slate-100 dark:border-slate-800"
                    >
                        <span className="truncate max-w-[80%] font-medium">
                            {bullets[0]?.text ? `• ${bullets[0].text}` : 'Overview collapsed • Click to expand synthesis'}
                        </span>
                        <span className="text-[11px] font-bold text-[#03488a] dark:text-[#a7c8ff] shrink-0 ml-2">
                            Expand ({bullets.length || 1} {bullets.length === 1 ? 'bullet' : 'bullets'})
                        </span>
                    </div>
                )}

                {/* ── Expanded Content ──────────────────────────────── */}
                {!isCollapsed && (
                    <>
                        {loading && !summaryData && (
                            <div className="space-y-2.5 py-2 animate-pulse">
                                <div className="h-3.5 bg-slate-200 dark:bg-slate-800 rounded w-11/12" />
                                <div className="h-3.5 bg-slate-200 dark:bg-slate-800 rounded w-4/5" />
                                <div className="h-3.5 bg-slate-200 dark:bg-slate-800 rounded w-9/12" />
                            </div>
                        )}

                        {error && !loading && (
                            <div className="py-2 px-3 rounded-lg bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 text-xs text-rose-700 dark:text-rose-300 flex items-center justify-between">
                                <span>{error}</span>
                                <button
                                    onClick={() => loadSummary(true)}
                                    className="font-bold underline hover:text-rose-900 cursor-pointer ml-2"
                                >
                                    Retry
                                </button>
                            </div>
                        )}

                        {!loading && bullets.length > 0 && (
                            <div className="space-y-3">
                                {/* Current & Latest Update Banner */}
                                {summaryData?.latestUpdate?.summary && (
                                    <div className="p-3 rounded-xl bg-gradient-to-r from-blue-50/90 via-indigo-50/60 to-purple-50/50 dark:from-blue-950/40 dark:via-indigo-950/30 dark:to-purple-950/20 border border-blue-200/80 dark:border-blue-800/60 shadow-2xs space-y-1.5 transition-all">
                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                            <div className="flex items-center gap-1.5">
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-blue-600 text-white shadow-2xs">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                                                    Current & Latest Update
                                                </span>
                                                {summaryData.latestUpdate.author && (
                                                    <span className="text-[11px] font-semibold text-slate-700 dark:text-slate-300">
                                                        by <FormattedBulletText text={summaryData.latestUpdate.author} allCandidates={allCandidates} />
                                                    </span>
                                                )}
                                            </div>
                                            {summaryData.latestUpdate.timestamp && (
                                                <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 bg-white/80 dark:bg-slate-900/60 px-2 py-0.5 rounded-md border border-slate-200/60 dark:border-slate-800">
                                                    {summaryData.latestUpdate.timestamp}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-slate-700 dark:text-slate-200 font-medium leading-relaxed pl-0.5">
                                            <FormattedBulletText
                                                text={summaryData.latestUpdate.summary}
                                                taskId={taskId}
                                                anchor={summaryData.latestUpdate.commentId ? { type: 'comment', id: summaryData.latestUpdate.commentId, label: 'Jump to update' } : null}
                                                jumpToComment={jumpToComment}
                                                jumpToAttachment={jumpToAttachment}
                                                allCandidates={allCandidates}
                                            />
                                        </div>
                                    </div>
                                )}

                                <ul className="space-y-2.5">
                                    {bullets.map((bullet, idx) => {
                                        const style = getBulletStyle(bullet.type);
                                        return (
                                            <li key={idx} className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-slate-300 leading-relaxed group">
                                                <span
                                                    className={`shrink-0 mt-0.5 w-5 h-5 rounded-md flex items-center justify-center ${style.bg} ${style.color} ${style.border} border shadow-2xs`}
                                                >
                                                    <span className="material-symbols-outlined text-xs">
                                                        {style.icon}
                                                    </span>
                                                </span>
                                                <div className="flex-1 min-w-0 pt-0.5 flex flex-col gap-0.5">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="flex-1 min-w-0">
                                                            {bullet.isLatestUpdate && (
                                                                <span className="inline-flex items-center gap-1 mr-1.5 px-1.5 py-0.2 rounded text-[9px] font-black uppercase tracking-wider bg-blue-100 dark:bg-blue-950/80 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 align-middle">
                                                                    Latest
                                                                </span>
                                                            )}
                                                            <FormattedBulletText
                                                                text={bullet.text}
                                                                fileRefs={bullet.fileRefs || []}
                                                                taskId={taskId}
                                                                anchor={bullet.anchor}
                                                                jumpToComment={jumpToComment}
                                                                jumpToAttachment={jumpToAttachment}
                                                                allCandidates={allCandidates}
                                                            />
                                                        </div>
                                                        {bullet.timestamp && (
                                                            <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 whitespace-nowrap shrink-0 mt-0.5">
                                                                {bullet.timestamp}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}

                        {/* ── Footer / Expanded Modal Trigger ───────────────── */}
                        <div className="pt-2 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between">
                            <span className="text-[11px] text-slate-400 dark:text-slate-500 font-medium">
                                Continuously synthesized across conversation, files, revisions & activity
                            </span>
                            <button
                                type="button"
                                onClick={() => onOpenFullSummary && onOpenFullSummary(summaryData)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-[#03488a] dark:text-[#a7c8ff] hover:bg-blue-50 dark:hover:bg-blue-950/60 transition-colors cursor-pointer"
                            >
                                <span>View Full Summary</span>
                                <span className="material-symbols-outlined text-sm">arrow_forward</span>
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
